import { InflateRequestMode } from '@sccs/types';
import { FilterCriteriaAlternative } from '../http/clients/dls/DlsClientAlternative';
import { FilterCriteriaDlsSearch } from '../http/clients/dls/DlsClientSearch';
import {
  FilterCriteriaCampaign,
  FilterCriteriaDlsTeaser,
  UnifiedTeaserFilterCriteria,
} from '../http/clients/dls/DlsClientTeaser';
import { OneSearchVehicleTileDataParams } from '../http/clients/onesearch/graphql/OneSearchGraphQL';
import { OneSearchPreferences } from '../http/clients/onesearch/OneSearchPreference';
import { OneSearchSearch } from '../http/clients/onesearch/OneSearchSearch';
import { OneSearchParameter } from '../types/GeneralParameter';
import {
  COMMERCIAL_VANS,
  vanEnumsCompatMap,
  vehicleAssortmentCommercial,
  vehicleCategoryCommercial,
} from './VansCompatMap';

type VanConfigTypes =
  | FilterCriteriaDlsSearch
  | UnifiedTeaserFilterCriteria
  | FilterCriteriaAlternative
  | OneSearchVehicleTileDataParams
  | OneSearchParameter;

export function isCommercialVanByModelId(modelIdentifier: string): boolean {
  return COMMERCIAL_VANS.includes(modelIdentifier);
}

export function isCommercialVanByMode(mode: InflateRequestMode): boolean {
  return [
    InflateRequestMode.PI_PAGES_VANS,
    InflateRequestMode.HOMEPAGE_VANS,
    InflateRequestMode.CAMPAIGN_TEASER_VANS,
  ].includes(mode);
}

export function isOneSearchSearch(parameter: VanConfigTypes): parameter is OneSearchSearch {
  return (parameter as OneSearchSearch).vehicleCategory !== undefined;
}

export function isOneSearchPreferenceType(parameter: VanConfigTypes): parameter is OneSearchPreferences {
  return (parameter as OneSearchPreferences).classFilters !== undefined;
}

export function isOneSearchType(parameter: VanConfigTypes): parameter is OneSearchParameter {
  return (parameter as OneSearchParameter).limit !== undefined;
}

// Two modes: handle single van and handle multiple vans
export function createVanConfiguration(parameter: VanConfigTypes) {
  const modelIdentifier = getModelIdentifier(parameter);
  const isMultiple = hasMultipleIdentifiers(modelIdentifier);
  return isMultiple
    ? createMultipleVansConfig(parameter, modelIdentifier)
    : createSingleVanConfig(parameter, modelIdentifier);
}

function createSingleVanConfig(parameter: VanConfigTypes, modelIdentifier: string) {
  const { identifier, body, assortment, fuelType, clusteringCriteria, vehicleCategory } =
    vanEnumsCompatMap[modelIdentifier];
  const baseConfig = {
    modelIdentifier: identifier,
    bodyType: body ?? '',
    fuelType: fuelType.join(','),
    clusteringCriteria: clusteringCriteria,
  };

  if (isOneSearchType(parameter)) {
    return {
      ...baseConfig,
      vehicleCategory: vehicleCategory,
    };
  } else {
    return {
      ...baseConfig,
      vehicleAssortment: assortment ?? vehicleAssortmentCommercial,
    };
  }
}

function createMultipleVansConfig(parameter: VanConfigTypes, modelIdentifier: string) {
  const sanitizedFuelTypeSet = new Set<string>();
  const sanitizedClusteringCriteriaSet = new Set<string>();
  const sanitizedIdentifier: string[] = [];
  const bodyType = isFilterCriteriaCampaign(parameter) ? parameter.bodyType : '';
  const modelIdentifierArray = stringToArray(modelIdentifier);
  let sanitizedVehicleAssortment = '';
  let sanitizedVehicleCategory = '';

  for (const modelId of modelIdentifierArray) {
    const { assortment, fuelType, clusteringCriteria, identifier, vehicleCategory } = vanEnumsCompatMap[modelId];
    fuelType.forEach((type) => sanitizedFuelTypeSet.add(type));
    sanitizedIdentifier.push(identifier);
    (clusteringCriteria ?? ['']).forEach((criteria) => sanitizedClusteringCriteriaSet.add(criteria));
    sanitizedVehicleAssortment = assortment ?? vehicleAssortmentCommercial;
    sanitizedVehicleCategory = vehicleCategory ?? vehicleCategoryCommercial;
  }

  const baseConfig = {
    modelIdentifier: sanitizedIdentifier.join(','),
    bodyType: bodyType,
    fuelType: Array.from(sanitizedFuelTypeSet).join(','),
    clusteringCriteria: Array.from(sanitizedClusteringCriteriaSet),
  };

  if (isOneSearchType(parameter)) {
    return {
      ...baseConfig,
      vehicleCategory: sanitizedVehicleCategory,
    };
  } else {
    return {
      ...baseConfig,
      vehicleAssortment: sanitizedVehicleAssortment,
    };
  }
}

function getModelIdentifier(parameter: VanConfigTypes): string {
  if (parameter as FilterCriteriaDlsSearch) {
    const modelIdentifier = (parameter as FilterCriteriaDlsSearch).modelIdentifier;
    const isArray = Array.isArray(modelIdentifier);
    return isArray ? modelIdentifier.join(',') : modelIdentifier;
  }
  if (parameter as FilterCriteriaDlsTeaser) {
    return (parameter as FilterCriteriaDlsTeaser).modelIdentifier ?? '';
  }
  if (parameter as FilterCriteriaAlternative) {
    return (parameter as FilterCriteriaAlternative).modelIdentifier ?? '';
  }
  if (parameter as OneSearchVehicleTileDataParams) {
    return (parameter as OneSearchVehicleTileDataParams).modelIdentifier?.[0] ?? '';
  }
  return '';
}

function hasMultipleIdentifiers(modelId: string): boolean {
  return Boolean(modelId.split(',').length > 1);
}

function stringToArray(modelId: string): string[] {
  return modelId.includes(',') ? modelId.split(',') : [modelId];
}

function isFilterCriteriaCampaign(
  criteria:
    | FilterCriteriaDlsSearch
    | UnifiedTeaserFilterCriteria
    | FilterCriteriaAlternative
    | OneSearchVehicleTileDataParams,
): criteria is FilterCriteriaCampaign {
  return 'campaign' in criteria;
}
